import React, { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, Star, Crown, Lock, Check, Trophy, Compass, Anchor, Heart, Sun, Flame, Award } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const ICONS = { Sparkles, Star, Crown, Compass, Anchor, Heart, Sun, Flame };

const CATEGORY_LABELS = {
  referral: "Empfehlungen",
  depth: "Tiefe",
  commitment: "Hingabe",
  visibility: "Sichtbarkeit",
  discipline: "Disziplin",
};

export default function PartnerAchievements() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("sat_token");
    if (!token) return;
    axios.get(`${API}/api/partners/me/achievements`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return null;

  const earnedCount = (data.milestones || []).filter((m) => m.earned).length + (data.special || []).filter((m) => m.earned).length;
  const total = data.total_milestones || ((data.milestones || []).length + (data.special || []).length);

  // Group special by category
  const specialByCat = {};
  (data.special || []).forEach((m) => {
    if (!specialByCat[m.category]) specialByCat[m.category] = [];
    specialByCat[m.category].push(m);
  });

  return (
    <div className="mb-12" data-testid="achievements-widget">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-2">Achievements</div>
          <h2 className="serif text-3xl md:text-4xl font-light">Deine Reise.</h2>
        </div>
        <div className="text-right">
          <div className="serif text-3xl font-light text-[#D4AF37]" data-testid="ach-count">
            {earnedCount}<span className="text-white/40 text-xl"> / {total}</span>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-white/45 mt-1">freigeschaltet</div>
        </div>
      </div>

      {data.next && (
        <div className="mb-8 p-5 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5" data-testid="ach-next">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Trophy size={18} className="text-[#D4AF37]" />
              <div>
                <div className="text-xs uppercase tracking-wider text-[#D4AF37]">Nächstes Empfehlungs-Ziel</div>
                <div className="serif text-lg mt-0.5">{data.next.label}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-white/85">Noch <b className="text-[#D4AF37]">{data.next.remaining}</b> {data.next.remaining === 1 ? "Mensch" : "Menschen"}</div>
              <div className="text-[10px] uppercase tracking-wider text-white/45 mt-0.5">{data.helped_count} / {data.next.threshold}</div>
            </div>
          </div>
          <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full transition-all duration-700 rounded-full"
                 style={{ width: `${Math.min(100, (data.helped_count / data.next.threshold) * 100)}%`, background: "linear-gradient(90deg,#D4AF37,#a48cff)" }} />
          </div>
        </div>
      )}

      {/* Standard (commission) milestones */}
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 mb-3">Empfehlungs-Meilensteine</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="ach-grid">
          {data.milestones?.map((m) => <Card key={m.key} m={m} hasProgress />)}
        </div>
      </div>

      {/* Special milestones grouped by category */}
      {data.special?.length > 0 && (
        <div className="space-y-8" data-testid="ach-special-section">
          {Object.entries(specialByCat).map(([cat, items]) => (
            <div key={cat}>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 mb-3">
                Lebenstaten · {CATEGORY_LABELS[cat] || cat}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {items.map((m) => <Card key={m.key} m={m} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Card = ({ m, hasProgress = false }) => {
  const Icon = ICONS[m.icon] || Star;
  const earned = m.earned;

  const downloadCert = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("sat_token");
    try {
      const r = await axios.get(`${API}/api/partners/me/achievements/${m.key}/certificate`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `SAT-Achievement-${m.key}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      alert("Zertifikat konnte nicht geladen werden.");
    }
  };

  return (
    <div className={`relative rounded-xl border p-5 text-center transition ${
            earned ? "bg-white/[0.04]" : "bg-white/[0.01] border-white/10 opacity-60 hover:opacity-100"
          }`}
          style={earned ? { borderColor: m.color, boxShadow: `0 0 24px ${m.color}25` } : {}}
          data-testid={`ach-card-${m.key}`}>
      <div className="flex justify-center mb-3">
        {earned ? (
          <div className="relative">
            <Icon size={28} style={{ color: m.color }} />
            <Check size={12} className="absolute -top-1 -right-2 text-[#48d597]" />
          </div>
        ) : (
          <Lock size={22} className="text-white/30" />
        )}
      </div>
      <div className="serif text-base leading-tight" style={{ color: earned ? m.color : "#ffffff70" }}>
        {m.label}
      </div>
      <div className="text-[10px] uppercase tracking-wider mt-2" style={{ color: earned ? m.color : "#ffffff45" }}>
        {earned ? "Erreicht" : (hasProgress && m.threshold ? `Bei ${m.threshold}` : "Verborgen")}
      </div>
      {!earned && hasProgress && m.progress > 0 && (
        <div className="mt-3 h-1 bg-white/5 rounded-full overflow-hidden">
          <div className="h-full bg-[#D4AF37]" style={{ width: `${m.progress * 100}%` }} />
        </div>
      )}
      {earned && (
        <button onClick={downloadCert}
                className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider hover:underline transition"
                style={{ color: m.color }}
                data-testid={`cert-download-${m.key}`}>
          <Award size={11} /> Zertifikat
        </button>
      )}
    </div>
  );
};
